import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  VerifiedUserOutlined as VerifiedIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  ContentCopyOutlined as CopyIcon,
  CheckRounded as CheckRoundedIcon,
  ErrorOutlineOutlined as ErrorOutlineIcon,
} from '@mui/icons-material';

import DoneIcon from '@mui/icons-material/Done';
import axios from 'axios';
import { apiConfigNhia } from '../BaseURL';
import { BRAND, brandButtonSx, brandGhostButtonSx, brandFieldSx } from '../../brand';

// ============================================================================
//  CCC generation — NHIA member validation
// ============================================================================
//
//  This dialog is the one place staff meet an external authority mid-
//  registration, and it looked like a debug tool: a bright green bar in a navy
//  product, a four-point instruction panel occupying the top half of the screen
//  on every single use, a hard 600px minimum height that left a band of empty
//  white under a two-field form, and — on success — the member's details
//  rendered as monospace text inside a green Alert, with the confirm button
//  duplicated inside the results AND in the footer.
//
//  The returned data is the point of the screen, so it is now the thing that
//  looks designed: the CCC set out on its own with a copy button, the member
//  identified by name and eligibility status, and the rest as a clean field
//  grid. The instructions are still here, one tap away, but collapsed — they
//  are read once and then never again.
//
//  A note on layout: CSS Grid throughout, never MUI's <Grid>. Under MUI 7 the
//  `<Grid item xs={12} sm={6}>` API is silently ignored, which is why every
//  detail in the old results panel rendered full width in one long column.

const CARD_TYPES = [
  { value: 'GHANACARD', label: 'Ghana Card',  hint: 'Verify by Ghana Card number' },
  { value: 'NHISCARD',  label: 'NHIS Card',   hint: 'Verify by NHIS membership number' },
];

const GUIDE = [
  'Choose Ghana Card or NHIS Card, depending on what the patient presents.',
  'Enter the number from that card. The membership number from the registration form is filled in for you.',
  'The system checks the member’s status with NHIA and generates the claim check code.',
  'On success the member’s details and eligibility dates come back with the code.',
];

const initials = (name = '') => {
  const words = String(name).replace(/[^A-Za-z ]/g, ' ').split(/\s+/).filter(Boolean);
  if (!words.length) return '—';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

// ── A labelled value in the results grid ────────────────────────────────────
function Detail({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: '.6px',
          textTransform: 'uppercase', color: BRAND.inkFaint, lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: BRAND.ink, mt: .125, wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

const Ccc = ({ open, onClose, memberNumber, onSubmit }) => {

  const [loading, setLoading]     = useState(false);
  const [response, setResponse]   = useState(null);
  const [error, setError]         = useState(null);
  const [cardType, setCardType]   = useState('');
  const [cardNo, setCardNo]       = useState(memberNumber || '');
  const [apiConfig, setApiConfig] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [copied, setCopied]       = useState(false);

  // This component is mounted two different ways.
  //
  //  · From the registration screens, as a dialog — they pass `open`, `onClose`
  //    and `onSubmit`.
  //  · As the "CCC" item in the Medical Records sidebar, which renders <Ccc />
  //    with NO props at all (see Record.js). There `open` is undefined, so the
  //    dialog was never open and that menu item showed a blank page.
  //
  // An undefined `open` is a reliable signal for the second case, since the
  // registration screens always pass a boolean. Standing open makes the tab
  // work, and the callbacks below are all optional so nothing throws when the
  // parent did not supply them.
  const standalone = open === undefined;
  const isOpen     = standalone ? true : open;

  // The membership number typed on the registration form is the same number
  // this lookup needs, so it is carried in rather than typed a second time.
  //
  // This has to be an effect, not just a useState initialiser. The dialog is
  // mounted once by the registration screen and only toggled with `open`, so an
  // initialiser runs before the number has been typed and never runs again —
  // which is why the field always came up empty no matter what was on the form
  // behind it.
  useEffect(() => {
    if (isOpen) {
      setCardNo(memberNumber || '');
      setCopied(false);
    }
  }, [isOpen, memberNumber]);

  // Fetch API config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(apiConfigNhia);
        if (response.data.success) {
          setApiConfig(response.data.config);
        } else {
          setError('Failed to load API configuration');
        }
      } catch (err) {
        console.error('Failed to fetch API config:', err);
        setError('Failed to load API configuration');
      }
    };
    fetchConfig();
  }, []);

  const callNHIAApi = async (requestData) => {
    if (!apiConfig) {
      setError('API configuration not loaded');
      return;
    }
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const url = `${apiConfig.base_url}${apiConfig.endpoint}`;

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-nhia-apikey': apiConfig.api_key,
          'x-nhia-apisecret': apiConfig.api_secret,
        },
        body: JSON.stringify(requestData)
      };

      const apiResponse = await fetch(url, requestOptions);

      if (!apiResponse.ok) {
        throw new Error(`API call failed: ${apiResponse.status} ${apiResponse.statusText}`);
      }

      const data = await apiResponse.json();
      setResponse(data);

    } catch (err) {
      setError(err.message);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOK = () => {
    if (onSubmit) onSubmit(response);
    handleClose();
  };

  const handleSubmit = () => {
    // Guarded: cardNo used to be seeded straight from a prop that is undefined
    // on a fresh registration, so this threw on .trim() instead of showing the
    // message.
    if (!String(cardNo || '').trim()) {
      setError('Card Number is required');
      return;
    }

    // Previously an unselected card type went to NHIA as an empty CardType and
    // came back as an opaque API failure.
    if (!cardType) {
      setError('Select a card type first');
      return;
    }

    const requestData = {
      CardType: cardType,
      CardNo: String(cardNo).trim()
    };

    callNHIAApi(requestData);
  };

  const handleClose = () => {
    // Reset form state when closing
    setResponse(null);
    setError(null);
    setCardNo(memberNumber || '');
    setCardType('');
    setCopied(false);
    // Optional: standalone use has nowhere to close TO, so this doubles as
    // "start another lookup".
    if (onClose) onClose();
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(response.MobCCC);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // Clipboard is blocked on insecure origins; the code is on screen to be
      // read either way, so this is not worth interrupting anyone over.
    }
  };

  // A response without a code is NOT a success, whatever the transport said.
  // The old markup dropped every response into a green "success" Alert, so an
  // ineligible member read as a validated one until somebody noticed the CCC
  // line was blank.
  const validated = !!response?.MobCCC;

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      // Standing open as a page, the backdrop would dim the sidebar behind it
      // for no reason — there is nothing to go back to.
      hideBackdrop={standalone}
      PaperProps={{
        sx: { borderRadius: '14px', m: { xs: 1.5, sm: 4 }, overflow: 'hidden' },
      }}
    >
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.75,
          px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.25 },
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
          color: '#fff',
        }}
      >
        <Box
          sx={{
            width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
            background: 'rgba(255,255,255,.14)', display: 'grid', placeItems: 'center',
          }}
        >
          <VerifiedIcon sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: { xs: 15.5, sm: 17 }, fontWeight: 800, lineHeight: 1.3 }}>
            Member Validation &amp; CCC
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: .82, mt: .125 }}>
            Checks eligibility with NHIA and returns a claim check code
          </Typography>
        </Box>
        {!standalone && (
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{ color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,.18)' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, background: BRAND.canvas }}>

        {/* ── Lookup form ────────────────────────────────────────────────── */}
        <Box
          sx={{
            p: { xs: 1.75, sm: 2.25 },
            borderRadius: '12px',
            background: BRAND.surface,
            border: `1px solid ${BRAND.line}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: 1.75,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(0,1fr) minmax(0,1fr)' },
            }}
          >
            <FormControl fullWidth size="small" sx={brandFieldSx}>
              <InputLabel id="card-type-label" required>Card Type</InputLabel>
              <Select
                labelId="card-type-label"
                value={cardType}
                label="Card Type"
                onChange={(e) => { setCardType(e.target.value); setError(null); }}
                sx={{ borderRadius: '10px' }}
              >
                {CARD_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND.ink }}>
                        {t.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: BRAND.inkFaint }}>
                        {t.hint}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Pre-filled with the membership number already entered on the
                registration form, so the same number is not keyed twice. Still
                editable: a Ghana Card lookup uses a different number. */}
            <TextField
              fullWidth
              size="small"
              label="Card / Membership Number"
              required
              value={cardNo}
              onChange={(e) => { setCardNo(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleSubmit(); }}
              placeholder={cardType === 'GHANACARD' ? 'Ghana Card number' : 'NHIS card number'}
              sx={brandFieldSx}
              helperText={
                memberNumber && cardNo === memberNumber
                  ? 'Carried over from the registration form'
                  : ' '
              }
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            disabled={loading || !String(cardNo || '').trim() || !cardType}
            startIcon={loading
              ? <CircularProgress size={17} sx={{ color: '#fff' }} />
              : <SendIcon sx={{ fontSize: 18 }} />}
            sx={{ ...brandButtonSx, mt: .5, py: 1.15 }}
          >
            {loading ? 'Validating with NHIA…' : 'Validate member & generate CCC'}
          </Button>
        </Box>

        {/* ── Error ──────────────────────────────────────────────────────── */}
        {error && (
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon sx={{ fontSize: 20 }} />}
            sx={{ mt: 2, borderRadius: '10px', fontSize: 13, alignItems: 'center' }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: .25 }}>
              Validation could not be completed
            </Typography>
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.55 }}>
              {error} — check the card type and number, then try again.
            </Typography>
          </Alert>
        )}

        {/* ── Result ─────────────────────────────────────────────────────── */}
        {response && (
          <Box
            sx={{
              mt: 2,
              borderRadius: '12px',
              background: BRAND.surface,
              border: `1px solid ${validated ? 'rgba(46,158,79,.35)' : 'rgba(224,139,47,.40)'}`,
              overflow: 'hidden',
            }}
          >
            {/* Member identity */}
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: { xs: 1.75, sm: 2.25 }, py: 1.75,
                borderBottom: `1px solid ${BRAND.line}`,
              }}
            >
              <Box
                sx={{
                  width: 44, height: 44, flexShrink: 0, borderRadius: '50%',
                  display: 'grid', placeItems: 'center',
                  background: validated ? BRAND.greenSoft : 'rgba(224,139,47,.12)',
                  color: validated ? BRAND.greenDark : '#A15C0E',
                  fontSize: 14, fontWeight: 800,
                }}
              >
                {initials(response.MemberName)}
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: BRAND.ink, lineHeight: 1.3 }}>
                  {response.MemberName || 'Member details unavailable'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .625, mt: .375 }}>
                  <Box
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: .375,
                      px: .875, py: .25, borderRadius: '6px',
                      background: validated ? BRAND.greenSoft : 'rgba(224,139,47,.12)',
                      border: `1px solid ${validated ? 'rgba(46,158,79,.28)' : 'rgba(224,139,47,.30)'}`,
                    }}
                  >
                    {validated
                      ? <CheckRoundedIcon sx={{ fontSize: 13, color: BRAND.greenDark }} />
                      : <ErrorOutlineIcon sx={{ fontSize: 13, color: '#A15C0E' }} />}
                    <Typography
                      sx={{
                        fontSize: 11, fontWeight: 800, letterSpacing: '.3px',
                        color: validated ? BRAND.greenDark : '#A15C0E',
                      }}
                    >
                      {response.Status || (validated ? 'Validated' : 'No code returned')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* The claim check code — the reason this dialog exists, so it is
                the largest thing in it rather than one monospace line among
                ten. */}
            {validated ? (
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  mx: { xs: 1.75, sm: 2.25 }, my: 2,
                  px: 2, py: 1.75,
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
                  color: '#fff',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 9.5, fontWeight: 800, letterSpacing: '.8px',
                      textTransform: 'uppercase', opacity: .72,
                    }}
                  >
                    Claim check code
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 24, sm: 28 }, fontWeight: 800,
                      letterSpacing: '.14em', lineHeight: 1.25,
                      fontFamily: '"Roboto Mono","Consolas",monospace',
                      wordBreak: 'break-all',
                    }}
                  >
                    {response.MobCCC}
                  </Typography>
                </Box>
                <Tooltip title={copied ? 'Copied' : 'Copy code'}>
                  <IconButton
                    onClick={copyCode}
                    aria-label="Copy claim check code"
                    sx={{
                      color: '#fff', flexShrink: 0,
                      background: 'rgba(255,255,255,.12)',
                      '&:hover': { background: 'rgba(255,255,255,.22)' },
                    }}
                  >
                    {copied
                      ? <CheckRoundedIcon sx={{ fontSize: 19 }} />
                      : <CopyIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Alert
                severity="warning"
                sx={{ mx: { xs: 1.75, sm: 2.25 }, my: 2, borderRadius: '10px', fontSize: 12.5 }}
              >
                NHIA responded, but returned no claim check code for this member.
                They may not be eligible for cover today.
              </Alert>
            )}

            {/* Everything else NHIA sent back */}
            <Box
              sx={{
                display: 'grid',
                gap: 1.75,
                gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(3, minmax(0,1fr))' },
                px: { xs: 1.75, sm: 2.25 }, pb: 2.25,
              }}
            >
              <Detail label="Gender"          value={response.Gender} />
              <Detail label="Date of birth"   value={response.DateOfBirth} />
              <Detail label="Membership no."  value={response.HIN} />
              <Detail label="Eligible from"   value={response.EligibilityStartDate} />
              <Detail label="Eligible to"     value={response.EligibilityEndDate} />
              <Detail label="Attendance date" value={response.AttendanceDate} />
              <Detail label="Transaction ID"  value={response.TransactionID} />
              <Detail label="Health provider" value={response.HPName} />
            </Box>
          </Box>
        )}

        {/* ── How this works ─────────────────────────────────────────────
            Kept, but collapsed. As a permanently expanded four-point panel it
            filled the top half of the dialog every time the screen was opened,
            including the several-hundredth time. */}
        <Box sx={{ mt: 2 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setShowGuide((v) => !v)}
            aria-expanded={showGuide}
            sx={{
              display: 'flex', alignItems: 'center', gap: .875, width: '100%',
              px: 1.5, py: 1, border: 0, cursor: 'pointer', font: 'inherit',
              borderRadius: '10px', background: 'transparent',
              color: BRAND.inkMuted, textAlign: 'left',
              '&:hover': { background: 'rgba(15,31,61,.04)' },
            }}
          >
            <HelpIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 700 }}>
              How this works
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18, flexShrink: 0,
                transform: showGuide ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s ease',
              }}
            />
          </Box>

          <Collapse in={showGuide}>
            <Box
              component="ol"
              sx={{
                m: 0, mt: .5, pl: 0, listStyle: 'none',
                display: 'grid', gap: 1,
                px: 1.75, py: 1.75,
                borderRadius: '10px',
                background: BRAND.surface,
                border: `1px solid ${BRAND.line}`,
              }}
            >
              {GUIDE.map((line, i) => (
                <Box key={i} component="li" sx={{ display: 'flex', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 18, height: 18, flexShrink: 0, mt: .125,
                      borderRadius: '50%', display: 'grid', placeItems: 'center',
                      background: BRAND.navySoft, color: BRAND.navy,
                      fontSize: 10.5, fontWeight: 800,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.6 }}>
                    {line}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      </DialogContent>

      {/* ── Footer ───────────────────────────────────────────────────────
          One confirm, not two. The old layout had an "Ok Done" button nested
          inside the results panel AND another in the footer. */}
      <DialogActions
        sx={{
          px: { xs: 2, sm: 2.5 }, py: 2, gap: 1.25,
          background: BRAND.surface,
          borderTop: `1px solid ${BRAND.line}`,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& > *': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{ ...brandGhostButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}
        >
          {standalone ? 'Clear' : 'Cancel'}
        </Button>

        {/* Standalone there is no registration form waiting for the result, so
            there is nothing to hand it back to. */}
        {response && !standalone && (
          <Button
            variant="contained"
            onClick={handleOK}
            disabled={!validated}
            startIcon={<DoneIcon sx={{ fontSize: 18 }} />}
            sx={{ ...brandButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}
          >
            Use these details
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};


export default Ccc
